import React, { useContext, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import Email from "@mui/icons-material/Email"
import WhatsApp from "@mui/icons-material/WhatsApp"
import { ApiContextDominios } from "../../context/apiContextDominios.js"

const ID_CLIENTE_GENERICO = 1
const VERDE_WHATSAPP = "#25D366"

const nombreCliente = (cliente) => [cliente?.descripcion, cliente?.apellido].filter(Boolean).join(" ") || "Cliente"

const blobABase64 = (blob) =>
  new Promise((resolve, reject) => {
    const lector = new FileReader()
    lector.onload = () => {
      const texto = String(lector.result || "")
      const coma = texto.indexOf(",")
      resolve(coma === -1 ? texto : texto.slice(coma + 1))
    }
    lector.onerror = () => reject(new Error("No se pudo leer el PDF"))
    lector.readAsDataURL(blob)
  })

/**
 * Mail o WhatsApp del PDF que ya está abierto en el visor.
 * `compartir`: { tipo: "pago" | "cobro", idCobro?, idsCobro?, cliente, detalle }
 */
const CompartirReciboPdfDialog = ({ open, onClose, blob, compartir }) => {
  const { enviarServiciosPagoMail, enviarServiciosPagoWhatsapp, enviarServiciosCobroMail, enviarServiciosCobroWhatsapp } =
    useContext(ApiContextDominios)
  const [canal, setCanal] = useState(null)
  const [destino, setDestino] = useState("")
  const [guardarEnCliente, setGuardarEnCliente] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState("")
  const [resultado, setResultado] = useState(null)

  const esMail = canal === "mail"
  const cliente = compartir?.cliente || null
  const contactoActual = esMail ? cliente?.email : cliente?.telefono
  const puedeGuardar =
    cliente && cliente.id && cliente.id !== ID_CLIENTE_GENERICO && destino.trim() && destino.trim() !== (contactoActual || "")

  useEffect(() => {
    if (!open) return
    setCanal(null)
    setDestino("")
    setGuardarEnCliente(false)
    setEnviando(false)
    setError("")
    setResultado(null)
  }, [open])

  const elegirCanal = (nuevo) => {
    setCanal(nuevo)
    setError("")
    setResultado(null)
    setGuardarEnCliente(false)
    const actual = nuevo === "mail" ? cliente?.email : cliente?.telefono
    setDestino(actual || "")
  }

  const enviar = async () => {
    setError("")
    if (!destino.trim()) return setError(esMail ? "Escribí el email" : "Escribí el teléfono")
    if (!blob) return setError("No hay un PDF para enviar")
    const pestania = esMail ? null : window.open("", "_blank")
    setEnviando(true)
    try {
      const pdfBase64 = await blobABase64(blob)
      const datos = { pdfBase64, guardarEnCliente: Boolean(puedeGuardar && guardarEnCliente) }
      const esPago = compartir?.tipo === "pago"
      const respuesta = esMail
        ? esPago
          ? await enviarServiciosPagoMail(compartir.idCobro, { ...datos, email: destino.trim() })
          : await enviarServiciosCobroMail({ ...datos, email: destino.trim(), idsCobro: compartir.idsCobro })
        : esPago
          ? await enviarServiciosPagoWhatsapp(compartir.idCobro, { ...datos, telefono: destino.trim() })
          : await enviarServiciosCobroWhatsapp({ ...datos, telefono: destino.trim(), idsCobro: compartir.idsCobro })
      if (!respuesta.ok) {
        pestania?.close()
        return setError(respuesta.message)
      }
      if (pestania) pestania.location.href = respuesta.data.waLink
      setResultado(respuesta.data)
    } catch (_) {
      pestania?.close()
      setError("No se pudo preparar el recibo")
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
        {canal ? esMail ? <Email sx={{ color: "rgb(244, 177, 14)" }} /> : <WhatsApp sx={{ color: VERDE_WHATSAPP }} /> : null}
        {canal ? `Enviar recibo por ${esMail ? "mail" : "WhatsApp"}` : "Compartir recibo por:"}
      </DialogTitle>
      <DialogContent>
        {!canal ? (
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            <Button
              variant="outlined"
              startIcon={<Email />}
              onClick={() => elegirCanal("mail")}
              sx={{ textTransform: "none", flex: 1, borderColor: "divider", color: "text.primary" }}
            >
              Mail
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<WhatsApp />}
              onClick={() => elegirCanal("whatsapp")}
              sx={{ textTransform: "none", flex: 1 }}
            >
              WhatsApp
            </Button>
          </Stack>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Box sx={{ bgcolor: "rgba(33, 150, 243, 0.06)", borderRadius: 1, p: 1.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {nombreCliente(cliente)}
              </Typography>
              {compartir?.detalle && (
                <Typography variant="caption" color="text.secondary">
                  {compartir.detalle}
                </Typography>
              )}
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
            {resultado ? (
              <Alert severity="success">
                {esMail ? (
                  resultado.message
                ) : (
                  <>
                    WhatsApp listo para enviar.{" "}
                    <Link href={resultado.waLink} target="_blank" rel="noopener noreferrer">
                      Abrir WhatsApp
                    </Link>{" "}
                    si no se abrió solo.
                  </>
                )}
                {resultado.contactoGuardado && " Se guardó el dato en el cliente."}
              </Alert>
            ) : (
              <>
                <TextField
                  autoFocus
                  size="small"
                  fullWidth
                  label={esMail ? "Email" : "Teléfono"}
                  type={esMail ? "email" : "tel"}
                  value={destino}
                  onChange={(evento) => setDestino(evento.target.value)}
                  onKeyDown={(evento) => evento.key === "Enter" && enviar()}
                  helperText={
                    esMail
                      ? "Se envía con el recibo en PDF adjunto"
                      : "Con código de país, sin espacios (ej. 5491122334455). El link dura 24 horas."
                  }
                />
                {puedeGuardar && (
                  <FormControlLabel
                    control={<Checkbox size="small" checked={guardarEnCliente} onChange={(evento) => setGuardarEnCliente(evento.target.checked)} />}
                    label={
                      <Typography variant="body2">
                        Guardar {esMail ? "este email" : "este teléfono"} en la ficha del cliente
                      </Typography>
                    }
                  />
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enviando} sx={{ textTransform: "none", color: "text.primary" }}>
          {resultado || !canal ? "Cerrar" : "Cancelar"}
        </Button>
        {canal && !resultado && (
          <Button
            variant="contained"
            onClick={enviar}
            disabled={enviando || !blob}
            startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : esMail ? <Email /> : <WhatsApp />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: esMail ? "rgb(244, 177, 14)" : VERDE_WHATSAPP,
              "&:hover": { bgcolor: esMail ? "#e0a40c" : "#1ebe5b" },
            }}
          >
            Enviar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default CompartirReciboPdfDialog
